import React, {useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField,
        Typography} from "@mui/material";

import {Blue3, Grey5, Grey6} from "../../theme/styles";
import {Calculation, EXPRESSION_OPERATORS, Expression} from "./querySpec";
import {DatasetField} from "./reportApi";

/**
 * Building a calculation — as a tree, because there is nowhere here to type a formula.
 *
 * THAT IS THE DESIGN, NOT A LIMITATION OF THE EDITOR. If this screen had a text box for arithmetic,
 * something would have to parse it, and whatever parsed it would be one bug away from handing the
 * database a string somebody typed. So the only thing it can produce is a tree of nodes the server
 * already knows how to refuse.
 *
 * ONE OPERATION, TWO OPERANDS, optionally rounded. The contract supports arbitrary nesting and the
 * server compiles it to eight levels — this editor deliberately offers the shape people actually
 * ask for: a difference, a ratio, a rate per unit. A full expression builder is a real piece of
 * work and offering half of one is worse than offering this honestly.
 *
 * ONLY NUMERIC FIELDS ARE OFFERED, because the server refuses arithmetic on text and a picker that
 * let somebody choose Department and then reported a refusal would be teaching them nothing.
 */

type Props = {
    open: boolean;
    fields: DatasetField[];
    onCancel: () => void;
    onSave: (calculation: Calculation) => void;
};

type Operand = {kind: "field"; field: string} | {kind: "literal"; value: string};

function operandExpression(operand: Operand): Expression | null {
    if (operand.kind === "field") {
        return operand.field ? {kind: "field", field: operand.field} : null;
    }
    const text = operand.value.trim();
    // A NUMBER OR NOTHING. The server refuses a non-numeric literal outright rather than escaping
    // it, so offering to send one would only produce a refusal the reader cannot act on.
    if (!/^-?\d+(\.\d+)?$/.test(text)) return null;
    return {kind: "literal", value: Number(text)};
}

export default function CalculationEditor({open, fields, onCancel, onSave}: Props) {
    const numeric = fields.filter(one => one.Role === "measure");

    const [label, setLabel] = useState("");
    const [left, setLeft] = useState<Operand>({kind: "field", field: ""});
    const [operator, setOperator] = useState("subtract");
    const [right, setRight] = useState<Operand>({kind: "field", field: ""});
    const [decimals, setDecimals] = useState("");

    const leftExpression = operandExpression(left);
    const rightExpression = operandExpression(right);
    const ready = !!label.trim() && !!leftExpression && !!rightExpression;

    const build = (): Calculation => {
        let expression: Expression = {
            kind: "operator", operator, left: leftExpression!, right: rightExpression!,
        };
        const places = decimals.trim();
        if (/^\d$/.test(places)) {
            expression = {kind: "function", function: "round",
                          arguments: [expression, {kind: "literal", value: Number(places)}]};
        }
        return {label: label.trim(), expression};
    };

    const operandControl = (operand: Operand, set: (one: Operand) => void) => (
        <Box sx={{display: "flex", gap: 1, alignItems: "center"}}>
            <TextField select size="small" sx={{width: 110}} value={operand.kind}
                       onChange={event => set(event.target.value === "field"
                           ? {kind: "field", field: ""} : {kind: "literal", value: ""})}>
                <MenuItem value="field">Field</MenuItem>
                <MenuItem value="literal">Number</MenuItem>
            </TextField>
            {operand.kind === "field" ? (
                <TextField select size="small" sx={{width: 210}} value={operand.field}
                           onChange={event => set({kind: "field", field: event.target.value})}>
                    {numeric.map(one => (
                        <MenuItem key={one.Field} value={one.Field}>{one.Label}</MenuItem>
                    ))}
                </TextField>
            ) : (
                <TextField size="small" sx={{width: 210}} placeholder="a number"
                           value={operand.value}
                           onChange={event => set({kind: "literal", value: event.target.value})}/>
            )}
        </Box>
    );

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 17}}>New calculation</DialogTitle>
            <DialogContent>
                <Typography sx={{fontSize: 12.5, color: Grey5, mb: 2, maxWidth: "70ch"}}>
                    Arithmetic over two numbers, worked out for each record and then summarized like
                    any other measure. There is nowhere to type a formula on purpose: the report is
                    sent as a structure the server can check, never as text it would have to parse.
                </Typography>

                <TextField size="small" label="Call it" fullWidth sx={{mb: 2}} value={label}
                           onChange={event => setLabel(event.target.value)}/>

                <Box sx={{display: "flex", flexDirection: "column", gap: 1.5}}>
                    {operandControl(left, setLeft)}
                    <TextField select size="small" sx={{width: 110}} value={operator}
                               onChange={event => setOperator(event.target.value)}>
                        {EXPRESSION_OPERATORS.filter(one => one.phase === "v1").map(one => (
                            <MenuItem key={one.id} value={one.id}>{one.label}</MenuItem>
                        ))}
                    </TextField>
                    {operandControl(right, setRight)}
                </Box>

                <Box sx={{display: "flex", gap: 1, alignItems: "center", mt: 2}}>
                    <TextField size="small" sx={{width: 110}} label="Decimals" value={decimals}
                               onChange={event => setDecimals(event.target.value)}/>
                    <Typography sx={{fontSize: 12.5, color: Grey6}}>
                        Optional. Leave blank to keep every digit the database gives.
                    </Typography>
                </Box>

                {operator === "divide" ? (
                    <Typography sx={{fontSize: 12.5, color: Grey6, mt: 2}}>
                        Dividing by zero leaves that record blank rather than failing the report.
                    </Typography>
                ) : null}

                {numeric.length === 0 ? (
                    <Typography sx={{fontSize: 12.5, color: Blue3, mt: 2}}>
                        This dataset has no numbers to calculate with.
                    </Typography>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} sx={{textTransform: "none"}}>Cancel</Button>
                <Button disabled={!ready} onClick={() => onSave(build())}
                        sx={{textTransform: "none"}}>Add</Button>
            </DialogActions>
        </Dialog>
    );
}
